/**
 * Centralised keyboard + Bluetooth-remote mappings. Presenter remotes typically
 * emulate PageUp/PageDown or arrow keys, so those are first-class here. Mappings
 * are centralised now so they can be made user-configurable later.
 */
import type { ScrollControl } from '@goanna-im/shared';
import { NUDGE_LARGE, NUDGE_SMALL } from '@goanna-im/shared';

export type KeyIntent =
  | { kind: 'scroll'; control: ScrollControl }
  | { kind: 'toggleControls' }
  | { kind: 'exit' };

/** Translate a keyboard event into an intent, or null if unmapped. */
export function keyToIntent(e: KeyboardEvent): KeyIntent | null {
  // Bluetooth game-pad remote: the X button scrolls back (up the script),
  // the B button scrolls forward (advances). Match by key OR code to be safe.
  if (e.key === 'x' || e.key === 'X' || e.code === 'KeyX') {
    return { kind: 'scroll', control: { action: 'nudge', value: -NUDGE_SMALL } };
  }
  if (e.key === 'b' || e.key === 'B' || e.code === 'KeyB') {
    return { kind: 'scroll', control: { action: 'nudge', value: NUDGE_SMALL } };
  }
  switch (e.key) {
    case ' ':
    case 'Spacebar':
      return { kind: 'scroll', control: { action: 'toggle' } };
    case 'ArrowDown':
      return { kind: 'scroll', control: { action: 'nudge', value: NUDGE_SMALL } };
    case 'ArrowUp':
      return { kind: 'scroll', control: { action: 'nudge', value: -NUDGE_SMALL } };
    case 'PageDown':
      return { kind: 'scroll', control: { action: 'nudge', value: NUDGE_LARGE } };
    case 'PageUp':
      return { kind: 'scroll', control: { action: 'nudge', value: -NUDGE_LARGE } };
    case 'ArrowRight':
      return { kind: 'scroll', control: { action: 'sectionNext' } };
    case 'ArrowLeft':
      return { kind: 'scroll', control: { action: 'sectionPrev' } };
    case 'Home':
      return { kind: 'scroll', control: { action: 'top' } };
    case '+':
    case '=':
      return { kind: 'scroll', control: { action: 'speedUp' } };
    case '-':
    case '_':
      return { kind: 'scroll', control: { action: 'speedDown' } };
    case 'Escape':
      return { kind: 'exit' };
    case 'c':
    case 'C':
      return { kind: 'toggleControls' };
    default:
      return null;
  }
}
